import Ingredient, { IngredientInterface } from './ingredient';
import ProductOption, { OptionType, ProductOptionInterface } from './product-option';
import ProductType, { ProductTypeInterface } from './product-type';
import Step from './step';
import api from './api';
import { Availabilities, CardMedia, CustomizerMedia } from '@/utils/types';
import OrderFlow from './order-flow';

export enum ProductAvailability {
    PERMANENT = 'permanent',
    DAILY = 'daily',
}

export interface ProductInterface {
    id?: number;
    name: string;
    position: number;
    slug: string;
    description: string;
    is_preset: boolean;
    price: number | string;
    instructions: string;
    is_customisable: boolean;
    is_active: boolean;
    schedule: Availabilities;
    availability: ProductAvailability;
    in_stock: boolean;
    quantity?: number;
    ingredients: IngredientInterface[];
    options: ProductOptionInterface[];
    type: ProductTypeInterface;
    origin: ProductInterface | null;
    image: CardMedia & CustomizerMedia;
    updated_at?: string;
}

export default class Product implements ProductInterface {
    id?: number;
    name: string;
    position: number;
    slug: string;
    description: string;
    is_preset: boolean;
    price: number;
    instructions: string;
    is_customisable: boolean;
    is_active: boolean;
    in_stock: boolean;
    availability: ProductAvailability;
    schedule: Availabilities;
    quantity?: number;
    ingredients: Ingredient[];
    options: ProductOption[];
    type: ProductType;
    origin: Product | null = null;
    image: CardMedia & CustomizerMedia;
    updated_at?: string;
    constructor(product: ProductInterface) {
        this.id = product.id;
        this.name = product.name;
        this.position = product.position;
        this.slug = product.slug;
        this.description = product.description;
        this.is_preset = product.is_preset;
        this.price = parseFloat(<string>product.price);
        this.instructions = product.instructions;
        this.is_customisable = product.is_customisable;
        this.is_active = product.is_active ? true : false;
        this.in_stock = product.in_stock ? true : false;
        this.schedule = product.schedule;
        this.availability = product.availability;
        this.image = product.image;
        this.type = new ProductType(product.type);
        this.ingredients = product.ingredients ? Ingredient.createBatch(product.ingredients) : [];
        this.options = product.options ? ProductOption.createBatch(product.options) : [];
        if (product.quantity !== undefined) {
            this.quantity = product.quantity;
        }
        if (product.origin) {
            this.origin = new Product(product.origin);
        }
        if (product.updated_at) {
            this.updated_at = product.updated_at;
        }
    }

    static async all(): Promise<Product[]> {
        const response = await api.get<ProductInterface[]>({ name: 'product.index' });
        if (response.success) {
            return response.content as Product[];
        }
        return [];
    }

    static createBatch(products: ProductInterface[] = []): Product[] {
        let productInstances = [];
        for (let index = 0; index < products.length; index++) {
            productInstances.push(new Product(products[index]));
        }
        return productInstances as Product[];
    }

    public async update(): Promise<boolean> {
        const response = await api.post<null>(
            { name: 'product.update', parameters: { id: this.id } },
            {
                id: this.id,
                name: this.name,
                slug: this.slug,
                description: this.description,
                is_preset: this.is_preset,
                price: this.price,
                instructions: this.instructions,
                is_customisable: this.is_customisable,
                is_active: this.is_active,
                schedule: this.schedule,
                in_stock: this.in_stock,
                availability: this.availability,
                type: new ProductType(this.type),
                ingredients: Ingredient.createBatch(this.ingredients),
                options: ProductOption.createBatch(this.options),
            }
        );
        return response.success;
    }

    static async find(id: number): Promise<Product | null> {
        const response = await api.get<ProductInterface>({ name: 'product.show', parameters: { id } });
        if (response.success) {
            return new Product(response.content as ProductInterface);
        }
        return null;
    }

    /**
     * Returns the product's content in a readable string
     * @returns string A readable string with description, ingredients and options
     */
    public getContent(): string {
        const productContent = this.ingredients.map(({ quantifiable, quantity, name }: Ingredient) => {
            return quantifiable ? `${quantity} ${name}` : name;
        });
        const productOptions = this.options
            .filter(({ type, value }: ProductOption) => (type === OptionType.CHECKBOX && (value === 'true' || value === 'yes')) || (type !== OptionType.CHECKBOX && value && value.length >= 1))
            .map(({ type, label, accepted_values, value }: ProductOption) => {
                if (type === OptionType.RADIO) {
                    const radioOptions = accepted_values.split('\n').map((labelValueStr: string) => {
                        const [value, label] = labelValueStr.split(':');
                        return { value, label };
                    });
                    const selectedValue = radioOptions.find((labelValue) => labelValue.value === value);
                    return selectedValue ? `${label}: ${selectedValue.label}` : '';
                } else if (type === OptionType.CHECKBOX) {
                    return label;
                }
                return value;
            });

        const content = [productContent.join(', '), productOptions.join(', ')].filter(Boolean).join(', ');
        return `${this.description ? `${this.description}` : ''} ${content ? ' - ' + content : ''}`;
    }

    public getStepIngredients(step: Step): Ingredient[] {
        return step.ingredients.filter((stepIngredient) => this.ingredients.some((ingredient) => stepIngredient.id === ingredient.id));
    }

    public getStepOptions(step: Step): ProductOption[] {
        return step.product_options.filter((stepOption) => this.options.some((option) => stepOption.id === option.id));
    }

    public async getIngredientsByStep() {
        const orderFlow = await OrderFlow.find(this.type.order_flow_id);
        if (orderFlow) {
            const ingredientsByStep: { [key: string]: Ingredient[] } = {};
            for (let step of orderFlow.steps) {
                ingredientsByStep[step.name] = this.getStepIngredients(step);
            }
            return ingredientsByStep;
        }
    }

    public async getOptionsByStep() {
        const orderFlow = await OrderFlow.find(this.type.order_flow_id);
        if (orderFlow) {
            const optionsByStep: { [key: string]: ProductOption[] } = {};
            for (let step of orderFlow.steps) {
                optionsByStep[step.name] = this.getStepOptions(step);
            }
            return optionsByStep;
        }
    }

    get productPrice() {
        const ingredients = this.ingredients;
        const options = this.options;
        let total = this.origin ? this.origin.price : this.price;
        if (ingredients && ingredients.length > 0) {
            total += ingredients
                .map((ingredient) => {
                    if (ingredient.quantity) {
                        return ingredient.quantity * ingredient.price;
                    }
                    return ingredient.price;
                })
                .reduce((accumulator: number, price: number) => accumulator + price, 0);
        }
        if (options && options?.length > 0) {
            total +=
                options
                    ?.map((option) => {
                        if (option.price && option.value) {
                            return option.price;
                        }
                        return 0;
                    })
                    .reduce((accumulator: number, price: number) => accumulator + price) ?? 0;
        }
        return total;
    }
}
